import { useState, useEffect, useCallback } from 'react';
import { JotformSubmission } from '@/pages/reports/types';
import jotformApi from '@/utils/jotformApi';
import { useToast } from '@/components/ui/use-toast';
import { toast } from 'sonner';

interface UseJotformSubmissionsProps {
  formIds: string[];
  teamId?: string;
  autoFetch?: boolean;
  selectedFormId?: string;
}

export const useJotformSubmissions = ({ 
  formIds, 
  teamId, 
  autoFetch = true,
  selectedFormId = 'all'
}: UseJotformSubmissionsProps) => {
  const [submissions, setSubmissions] = useState<JotformSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [formCategories, setFormCategories] = useState<Record<string, string>>({});
  const { toast: uiToast } = useToast();

  // Load form categories from localStorage
  useEffect(() => {
    try {
      // Try to load from localStorage first (jotformCategories from normal reports)
      const storedCategories = localStorage.getItem('jotformCategories');
      if (storedCategories) {
        setFormCategories(JSON.parse(storedCategories));
        return;
      }
      
      // Also try to load from alertsJotformCategories (from alerts page)
      const alertsCategories = localStorage.getItem('alertsJotformCategories');
      if (alertsCategories) {
        setFormCategories(JSON.parse(alertsCategories));
      }
    } catch (err) {
      console.error('Error loading form categories:', err);
    }
  }, []);

  const fetchSubmissions = useCallback(async (apiKey?: string) => {
    // If no specific form is selected or the selected form is not in the formIds, don't fetch anything
    if (selectedFormId === 'all' || !formIds.length || (selectedFormId !== 'all' && !formIds.includes(selectedFormId))) {
      setSubmissions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // If an API key is provided, set it before making requests
      if (apiKey) {
        jotformApi.setApiKey(apiKey);
      }

      // Only fetch the selected form - ignore the other forms in formIds
      const formIdToFetch = selectedFormId;
      
      console.log(`Fetching only submissions for form ${formIdToFetch}`);
      
      // Add a retry mechanism with exponential backoff but fewer retries
      let fetchedSubmissions: JotformSubmission[] = [];
      let maxRetries = 2; // Reduced from 3 to 2 for faster feedback
      let retryDelay = 1500; // Reduced from 2000 to 1500ms
      let retryCount = 0;
      let success = false;
      
      while (retryCount < maxRetries && !success) {
        try {
          console.log(`Attempt ${retryCount + 1}: Fetching submissions for form ${formIdToFetch}`);
          // Direct call to getFormSubmissions - reduce limit to 10 to make it faster and less likely to hit resource limits
          fetchedSubmissions = await jotformApi.getFormSubmissions(formIdToFetch, 10); 
          success = true;
        } catch (err: any) {
          // Check if this is a CORS error
          if (err.message?.includes('Failed to fetch') || err.toString().includes('ERR_FAILED') || err.toString().includes('CORS')) {
            console.error('CORS error encountered:', err);
            throw new Error('CORS Error: The browser could not communicate with the Jotform API due to cross-origin restrictions.');
          }
          // Check if this is a resource error
          else if (err.message?.includes('ERR_INSUFFICIENT_RESOURCES') || 
              err.toString().includes('ERR_INSUFFICIENT_RESOURCES')) {
            retryCount++;
            console.log(`Resource limitation encountered, retrying (${retryCount}/${maxRetries})...`);
            
            // Show a toast for retries
            toast.info(`Retry attempt ${retryCount}/${maxRetries} - API resource limitation. Waiting ${retryDelay/1000} seconds...`);
            
            if (retryCount >= maxRetries) {
              throw new Error(`Failed after ${maxRetries} retries: API resource limitations. Try again later.`);
            }
            
            // Wait longer with each retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            retryDelay *= 1.5; // Reduced multiplier from 2 to 1.5
          } else {
            // Not a resource error, rethrow
            throw err;
          }
        }
      }
      
      // If a teamId is provided, assign it to all submissions
      const processedSubmissions = teamId 
        ? fetchedSubmissions.map(sub => ({ ...sub, team_id: teamId }))
        : fetchedSubmissions;

      // Sort submissions by date (newest first) using the submitted_at field
      const sortedSubmissions = processedSubmissions.sort((a, b) => {
        return new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime();
      });

      setSubmissions(sortedSubmissions);
      console.log(`Successfully fetched ${sortedSubmissions.length} submissions for form ID ${formIdToFetch}`);
      
      // Try to get the form title to update categories - but don't block on this
      try {
        const formTitle = await jotformApi.getFormTitle(formIdToFetch);
        if (formTitle) {
          // Update our form categories
          const updatedCategories = { ...formCategories, [formIdToFetch]: formTitle };
          setFormCategories(updatedCategories);
          // Save to localStorage
          localStorage.setItem('jotformCategories', JSON.stringify(updatedCategories));
        }
      } catch (err) {
        console.warn(`Could not fetch form title for ${formIdToFetch}:`, err);
        // Continue even if we couldn't get the form title
      }
      
    } catch (err: any) {
      console.error('Error fetching Jotform submissions:', err);
      setError(err);
      
      // Provide more specific error messages based on error type
      if (err.message?.includes('ERR_INSUFFICIENT_RESOURCES')) {
        toast.error(`API resource limit reached. Please try again later.`);
      } else if (err.message?.includes('API key is required')) {
        toast.error(`Jotform API key is missing. Please configure the integration.`);
      } else if (err.message?.includes('timed out')) {
        toast.error(`Request timed out. The Jotform API is currently unresponsive.`);
      } else if (err.message?.includes('CORS')) {
        toast.error(`CORS Error: The browser cannot access the Jotform API directly. Try using a proxy or backend solution.`);
      } else {
        toast.error(`Failed to fetch form submissions: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [formIds, teamId, selectedFormId, formCategories]);

  useEffect(() => {
    if (autoFetch && selectedFormId !== 'all') {
      // Add a small delay before fetching to avoid too many simultaneous requests
      const timer = setTimeout(() => {
        fetchSubmissions();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [fetchSubmissions, autoFetch, selectedFormId]);

  const setApiKey = useCallback((apiKey: string) => {
    jotformApi.setApiKey(apiKey);
    // Store the API key in localStorage for persistence
    localStorage.setItem('jotformApiKey', apiKey);
    
    // Only fetch if a form is selected
    if (selectedFormId !== 'all') {
      fetchSubmissions(apiKey);
    }
  }, [fetchSubmissions, selectedFormId]);

  useEffect(() => {
    // Try to get API key from localStorage on initial load
    const storedApiKey = localStorage.getItem('jotformApiKey');
    if (storedApiKey) {
      jotformApi.setApiKey(storedApiKey);
    }
  }, []);

  return {
    submissions,
    isLoading,
    error,
    refetch: fetchSubmissions,
    setApiKey,
    formCategories
  };
};
